import { 
  createGame, 
  joinGame, 
  getGameData,
  fetchGameData,
  updateScore, 
  recordTurn,
  updateLastTurn,
  setCurrentTurn,
  deleteGame,
  verifyPlayerInGame,
  updateGameStats,
  setGameWinner
} from './firebase.js';
import { getDatabase, ref, update } from 'firebase/database';

// Function to restart a game - reset scores and turn state
function restartGame(gameId) {
  const playerId = localStorage.getItem('playerId');
  const database = getDatabase();
  
  // Reset all players to initial scores based on game type
  fetchGameData(gameId).then(gameData => {
    const gameType = gameData.type;
    let initialScore = 0;
    if (gameType === '501') initialScore = 501;
    else if (gameType === '301') initialScore = 301;
    else if (gameType === '201') initialScore = 201;
    
    // Reset each player's score and turns
    Object.keys(gameData.players).forEach(pid => {
      update(ref(database, `games/${gameId}/players/${pid}`), {
        score: initialScore,
        turns: [],
        lastScores: []
      });
    });
    
    // Reset game state
    update(ref(database, `games/${gameId}`), {
      currentTurn: Object.keys(gameData.players)[0], // Set first player's turn
      winner: null,
      active: true,
      stats: {
        startTime: Date.now(),
        rounds: 0,
        highestScore: 0,
        totalScores: 0
      }
    });
  }).catch(error => {
    console.error('Error restarting game:', error);
  });
}

// Function to end the current game early
function endGame(gameId) {
  const database = getDatabase();
  update(ref(database, `games/${gameId}`), {
    active: false,
    currentTurn: null
  });
}

// Function to show game over screen with statistics
function showGameOverScreen(gameData) {
  const gameOverModal = document.getElementById('game-over-modal');
  const winnerId = gameData.winner;
  
  if (!winnerId || !gameData.players || !gameData.players[winnerId]) {
    console.error('Cannot show game over screen: winner not found');
    return;
  }
  
  const winner = gameData.players[winnerId];
  const stats = gameData.stats || {};
  const gameStartTime = stats.startTime || 0;
  const gameEndTime = stats.endTime || Date.now();
  const gameDuration = Math.floor((gameEndTime - gameStartTime) / 1000); // in seconds
  
  // Set winner name
  document.getElementById('winner-name').textContent = winner.name;
  
  // Calculate and display game stats
  const minutes = Math.floor(gameDuration / 60);
  const seconds = gameDuration % 60;
  document.getElementById('game-duration').textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  
  document.getElementById('total-rounds').textContent = stats.rounds || 0;
  document.getElementById('highest-score').textContent = stats.highestScore || 0;
  
  // Calculate average score
  const avgScore = stats.rounds > 0 ? Math.round(stats.totalScores / stats.rounds) : 0;
  document.getElementById('avg-score').textContent = avgScore;
  
  // Generate player rankings
  const playerRankings = document.getElementById('player-rankings');
  playerRankings.innerHTML = '<h4>Player Rankings</h4>';
  
  if (gameData.players) {
    // Sort players by score (ascending for countdown games, descending for count-up)
    const isCountdownGame = gameData.type === '501' || gameData.type === '301' || gameData.type === '201';
    const sortedPlayers = Object.entries(gameData.players).sort((a, b) => {
      if (isCountdownGame) {
        return a[1].score - b[1].score; // Lower score is better for countdown games
      } else {
        return b[1].score - a[1].score; // Higher score is better for count-up games
      }
    });
    
    // Check if we're on mobile
    const isMobile = window.innerWidth <= 600;
    
    // Limit player display on mobile to top 3 or 4 if there are many players
    const displayLimit = isMobile && sortedPlayers.length > 4 ? 3 : sortedPlayers.length;
    
    // Create ranking list
    sortedPlayers.slice(0, displayLimit).forEach((player, index) => {
      const [playerId, playerData] = player;
      const rankingItem = document.createElement('div');
      rankingItem.classList.add('ranking-item');
      
      // Add position classes for medal colors
      let positionClass = '';
      if (index === 0) positionClass = 'first';
      else if (index === 1) positionClass = 'second';
      else if (index === 2) positionClass = 'third';
      
      rankingItem.innerHTML = `
        <div class="ranking-position ${positionClass}">${index + 1}</div>
        <div class="ranking-name">${playerData.name}</div>
        <div class="ranking-score">${playerData.score}</div>
      `;
      
      playerRankings.appendChild(rankingItem);
    });
    
    // If we limited the players on mobile, add a message about hidden players
    if (isMobile && sortedPlayers.length > displayLimit) {
      const hiddenCount = sortedPlayers.length - displayLimit;
      const morePlayersMsg = document.createElement('div');
      morePlayersMsg.classList.add('more-players-msg');
      morePlayersMsg.textContent = `+ ${hiddenCount} more player${hiddenCount > 1 ? 's' : ''}`;
      playerRankings.appendChild(morePlayersMsg);
    }
  }
  
  // Show modal with a slight delay for better UX
  setTimeout(() => {
    gameOverModal.classList.remove('hidden');
    gameOverModal.classList.add('visible');
  }, 500);
  
  // Setup close button
  document.getElementById('close-game-over-modal').addEventListener('click', () => {
    gameOverModal.classList.remove('visible');
    gameOverModal.classList.add('hidden');
  });
  
  // Setup new game button
  document.getElementById('new-game-btn').addEventListener('click', () => {
    window.location.href = window.location.origin + window.location.pathname;
  });
  
  // Setup restart game button
  document.getElementById('restart-game-btn').addEventListener('click', () => {
    restartGame(gameData.id);
    gameOverModal.classList.remove('visible');
    gameOverModal.classList.add('hidden');
  });
  
  // Setup share results button
  document.getElementById('share-results-btn').addEventListener('click', () => {
    // Generate a text summary of the game results
    const gameType = gameData.type;
    const winnerName = winner.name;
    
    let shareText = `🎯 BadAim Dart Scoreboard Results 🎯\n\n`;
    shareText += `Game: ${gameData.name}\n`;
    shareText += `Type: ${gameType}\n`;
    shareText += `Winner: ${winnerName}\n\n`;
    shareText += `Final Scores:\n`;
    
    Object.values(gameData.players).forEach((player, index) => {
      shareText += `${index + 1}. ${player.name}: ${player.score}\n`;
    });
    
    shareText += `\nPlayed on BadAim: badaim.drgfunk.com`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareText).then(() => {
      alert('Game results copied to clipboard!');
    }).catch(err => {
      console.error('Could not copy results: ', err);
    });
  });
}

// Function to generate QR code for game URL
function generateQRCode(url, containerId = 'qrcode') {
  try {
    // Clear previous QR code if any
    const qrcodeElement = document.getElementById(containerId);
    if (!qrcodeElement) {
      console.error(`QR code container with ID '${containerId}' not found`);
      return;
    }
    qrcodeElement.innerHTML = '';
    
    // Generate QR code
    const qr = qrcode(0, 'M');
    qr.addData(url);
    qr.make();
    
    // Create image element
    const qrImage = qr.createImgTag(5); // 5 is the pixel size
    qrcodeElement.innerHTML = qrImage;
  } catch (error) {
    console.error('Error generating QR code:', error);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const urlParams = new URLSearchParams(window.location.search);
  const gameId = urlParams.get('gameId');
  
  if (gameId) {
    // Join existing game
    document.getElementById('join-game').classList.remove('hidden');
    document.getElementById('game-id-display').textContent = gameId;
    
    document.getElementById('join-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const playerName = document.getElementById('player-name').value.trim();
      
      if (playerName) {
        try {
          // Hide any previous error
          document.getElementById('join-error').classList.add('hidden');
          
          // Disable form while joining
          const joinButton = e.target.querySelector('button[type="submit"]');
          joinButton.disabled = true;
          joinButton.textContent = 'Joining...';
          
          const playerId = await joinGame(gameId, playerName);
          
          localStorage.setItem('playerId', playerId);
          localStorage.setItem('playerName', playerName);
          localStorage.setItem('gameId', gameId);
          
          showGameBoard(gameId);
        } catch (error) {
          console.error('Error joining game:', error);
          document.getElementById('join-error').classList.remove('hidden');
          
          // Re-enable form
          const joinButton = e.target.querySelector('button[type="submit"]');
          joinButton.disabled = false;
          joinButton.textContent = 'Join Game';
        }
      }
    });
  } else {
    // Create new game
    document.getElementById('create-game').classList.remove('hidden');
    
    document.getElementById('create-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const gameName = document.getElementById('game-name').value.trim();
      const gameType = document.querySelector('input[name="game-type"]:checked').value;
      const hostName = document.getElementById('host-name').value.trim();
      
      if (gameName && hostName) {
        try {
          const gameId = await createGame(gameName, gameType);
          const playerId = await joinGame(gameId, hostName);
          
          localStorage.setItem('playerId', playerId);
          localStorage.setItem('playerName', hostName);
          localStorage.setItem('gameId', gameId);
          
          // Update URL with game ID
          const gameUrl = `${window.location.origin}${window.location.pathname}?gameId=${gameId}`;
          window.history.pushState({ gameId }, '', gameUrl);
          
          // Show shareable link
          document.getElementById('share-link').value = gameUrl;
          document.getElementById('share-container').classList.remove('hidden');
          
          // Generate QR Code for the game URL
          generateQRCode(gameUrl);
          
          showGameBoard(gameId);
        } catch (error) {
          alert('Error creating game: ' + error.message);
        }
      }
    });
    
    // Copy share link button
    document.getElementById('copy-link').addEventListener('click', () => {
      const shareLink = document.getElementById('share-link');
      shareLink.select();
      document.execCommand('copy');
    });
  }
  
  // Check if user was in a game previously
  const existingPlayerId = localStorage.getItem('playerId');
  const existingPlayerName = localStorage.getItem('playerName');
  const existingGameId = localStorage.getItem('gameId');
  
  // Verify player exists in the game before auto-joining
  if (gameId && existingPlayerId && existingPlayerName && gameId === existingGameId) {
    console.log('Checking if player exists in game...');
    verifyPlayerInGame(gameId, existingPlayerId)
      .then(playerExists => {
        console.log('Player exists in game:', playerExists);
        if (playerExists) {
          showGameBoard(gameId);
        } else {
          // Player not found in game, clear localStorage and show join form
          localStorage.removeItem('playerId');
          localStorage.removeItem('playerName');
          localStorage.removeItem('gameId');
          document.getElementById('join-game').classList.remove('hidden');
        }
      })
      .catch(error => {
        console.error('Error verifying player:', error);
        // On error, show join form
        document.getElementById('join-game').classList.remove('hidden');
      });
  } else if (gameId) {
    console.log('New player joining game or different game ID');
    // Ensure join screen is visible if gameId is in URL but player isn't auto-joining
    document.getElementById('join-game').classList.remove('hidden');
  }
});

function showGameBoard(gameId) {
  // Hide setup screens
  document.getElementById('create-game').classList.add('hidden');
  document.getElementById('join-game').classList.add('hidden');
  
  // Show game board
  const gameBoard = document.getElementById('game-board');
  gameBoard.classList.remove('hidden');
  
  // Load game data
  const unsubscribe = getGameData(gameId, renderGame, (error) => {
    console.error('Error loading game data:', error);
    document.getElementById('game-board').classList.add('hidden');
    document.getElementById('join-game').classList.remove('hidden');
    document.getElementById('join-error').classList.remove('hidden');
  });
  
  // Setup turn handling
  setupTurnHandling(gameId);
  
  // Setup share game button
  setupShareGame(gameId);
  
  // Setup end game button
  document.getElementById('end-game').addEventListener('click', () => {
    if (confirm('Are you sure you want to end this game early?')) {
      endGame(gameId);
    }
  });
  
  // Setup restart game button
  document.getElementById('restart-game').addEventListener('click', () => {
    if (confirm('Are you sure you want to restart this game? All scores will be reset.')) {
      restartGame(gameId);
    }
  });
}

// Setup the share game functionality
function setupShareGame(gameId) {
  const shareGameBtn = document.getElementById('share-game');
  const shareModal = document.getElementById('share-modal');
  const closeShareModalBtn = document.getElementById('close-share-modal');
  const gameShareLink = document.getElementById('game-share-link');
  const copyGameLinkBtn = document.getElementById('copy-game-link');
  
  // Generate the game URL
  const gameUrl = `${window.location.origin}${window.location.pathname}?gameId=${gameId}`;
  
  // Set the share link input value
  gameShareLink.value = gameUrl;
  
  // Open modal when share button is clicked
  shareGameBtn.addEventListener('click', () => {
    shareModal.classList.remove('hidden');
    
    // Generate QR code for the game
    generateQRCode(gameUrl, 'game-qrcode');
  });
  
  // Close modal when close button is clicked
  closeShareModalBtn.addEventListener('click', () => {
    shareModal.classList.add('hidden');
  });
  
  // Close modal when clicking outside the modal content
  shareModal.addEventListener('click', (e) => {
    if (e.target === shareModal) {
      shareModal.classList.add('hidden');
    }
  });
  
  // Copy share link when copy button is clicked
  copyGameLinkBtn.addEventListener('click', () => {
    gameShareLink.select();
    document.execCommand('copy');
    
    // Visual feedback
    copyGameLinkBtn.textContent = 'Copied!';
    setTimeout(() => {
      copyGameLinkBtn.textContent = 'Copy';
    }, 2000);
  });
}

function renderGame(gameData) {
  const playerId = localStorage.getItem('playerId');
  const gameBoard = document.getElementById('game-board');
  
  // Check if game has a winner and show game over screen
  if (gameData.winner && !document.getElementById('game-over-modal').classList.contains('visible')) {
    showGameOverScreen(gameData);
  }
  
  // Update game name
  document.getElementById('current-game-name').textContent = gameData.name;
  
  // Render players and scores
  const playersList = document.getElementById('players-list');
  playersList.innerHTML = '';
  
  if (gameData.players) {
    Object.entries(gameData.players).forEach(([id, player]) => {
      const playerElement = document.createElement('div');
      playerElement.classList.add('player-card');
      
      if (id === gameData.currentTurn) {
        playerElement.classList.add('current-turn');
      }
      
      if (id === playerId) {
        playerElement.classList.add('current-player');
      }
      
      if (id === gameData.winner) {
        playerElement.classList.add('winner');
      }
      
      // Display game type-specific info
      let gameTypeInfo = '';
      if (gameData.type === '501' || gameData.type === '301' || gameData.type === '201') {
        const initialScore = gameData.type;
        const remaining = player.score;
        gameTypeInfo = `<div class="game-info">${initialScore}: ${remaining} remaining</div>`;
      }
      
      playerElement.innerHTML = `
        <h3>${player.name}</h3>
        <div class="score">${player.score}</div>
        ${gameTypeInfo}
        <div class="turns">
          ${player.turns ? Object.values(player.turns).map(turn => 
            `<span class="turn ${turn.bust ? 'bust' : ''}" title="${turn.dartScores ? turn.dartScores.join(' + ') : ''}${turn.bust ? ' (Bust)' : ''}">${turn.bust ? 'Bust' : turn.points}${turn.edited ? '*' : ''}</span>`
          ).join('') : ''}
        </div>
      `;
      
      playersList.appendChild(playerElement);
    });
  }
  
  // If game is over, disable controls
  if (gameData.winner) {
    document.getElementById('score-input-container').classList.add('hidden');
    document.getElementById('turn-status').textContent = 'Game Over!';
    return;
  }
  
  // Show/hide start game button depending on if the game has started
  document.getElementById('start-game').style.display = gameData.currentTurn ? 'none' : 'block';
  
  // Show/hide share game button once game has started
  document.getElementById('share-game').style.display = gameData.currentTurn ? 'none' : 'block';
  
  // Update turn indicator
  const isCurrentTurn = playerId === gameData.currentTurn;
  document.getElementById('score-input-container').classList.toggle('hidden', !isCurrentTurn);
  
  // Show/hide edit last score button
  const editButton = document.getElementById('edit-last-score');
  const currentPlayer = gameData.players && gameData.players[playerId];
  const hasPreviousTurns = currentPlayer && currentPlayer.turns && Object.keys(currentPlayer.turns).length > 0;
  editButton.style.display = hasPreviousTurns ? 'block' : 'none';
  
  if (isCurrentTurn) {
    document.getElementById('turn-status').textContent = 'Your turn!';
    
    // Clear dart inputs
    document.getElementById('dart1').value = '';
    document.getElementById('dart2').value = '';
    document.getElementById('dart3').value = '';
    document.getElementById('dart-total').textContent = '0';
    
    // If the player has lastScores, we can pre-fill with them for editing
    if (currentPlayer.lastScores && editButton.style.display !== 'none') {
      editButton.dataset.lastScores = JSON.stringify(currentPlayer.lastScores);
    }
    
    // Set limits based on game type
    if (gameData.type === '501' || gameData.type === '301' || gameData.type === '201') {
      // For countdown games, each dart is limited by remaining points or 60
      const maxPoint = Math.min(60, currentPlayer.score);
      document.getElementById('dart1').setAttribute('max', maxPoint);
      document.getElementById('dart2').setAttribute('max', maxPoint);
      document.getElementById('dart3').setAttribute('max', maxPoint);
      
      // Update label with total maximum
      const maxTotal = Math.min(180, currentPlayer.score);
      document.getElementById('score-label').textContent = `Enter your dart scores (max total: ${maxTotal}):`;
    } else {
      // For cricket/count-up, reset to default
      document.getElementById('dart1').setAttribute('max', 60);
      document.getElementById('dart2').setAttribute('max', 60);
      document.getElementById('dart3').setAttribute('max', 60);
      document.getElementById('score-label').textContent = 'Enter your dart scores:';
    }
  } else if (gameData.currentTurn) {
    const currentPlayer = gameData.players[gameData.currentTurn];
    document.getElementById('turn-status').textContent = `${currentPlayer.name}'s turn`;
  } else {
    document.getElementById('turn-status').textContent = 'Game waiting to start';
  }
}

function setupTurnHandling(gameId) {
  const scoreForm = document.getElementById('score-form');
  let isEditingLastScore = false;
  
  // Calculate total when any dart input changes
  const dart1Input = document.getElementById('dart1');
  const dart2Input = document.getElementById('dart2');
  const dart3Input = document.getElementById('dart3');
  const dartTotalEl = document.getElementById('dart-total');
  const bustButton = document.getElementById('bust-button');
  
  const calculateTotal = () => {
    const dart1 = parseInt(dart1Input.value) || 0;
    const dart2 = parseInt(dart2Input.value) || 0;
    const dart3 = parseInt(dart3Input.value) || 0;
    const total = dart1 + dart2 + dart3;
    dartTotalEl.textContent = total;
    return total;
  };
  
  dart1Input.addEventListener('input', calculateTotal);
  dart2Input.addEventListener('input', calculateTotal);
  dart3Input.addEventListener('input', calculateTotal);
  
  // Add bust button handler
  bustButton.addEventListener('click', async () => {
    const playerId = localStorage.getItem('playerId');
    
    try {
      // Disable the form while processing
      const submitButton = scoreForm.querySelector('button[type="submit"]');
      submitButton.disabled = true;
      bustButton.disabled = true;
      
      // Get current game data with one-time fetch
      const gameData = await fetchGameData(gameId);
      const player = gameData.players[playerId];
      const gameType = gameData.type;
      
      // Only process bust for countdown games
      const isCountdownGame = gameType === '501' || gameType === '301' || gameType === '201';
      if (!isCountdownGame) {
        alert('Bust only applies to countdown games');
        submitButton.disabled = false;
        bustButton.disabled = false;
        return;
      }
      
      // Create zero dart scores to represent the bust
      const dartScores = [0, 0, 0];
      const points = 0;
      
      // Record a turn with bust flag
      await recordTurn(gameId, playerId, points, dartScores, true);
      
      // Score remains the same on bust
      const newScore = player.score;
      
      // Move to next player
      const playerIds = Object.keys(gameData.players);
      const currentIndex = playerIds.indexOf(playerId);
      const nextIndex = (currentIndex + 1) % playerIds.length;
      const nextPlayerId = playerIds[nextIndex];
      
      await setCurrentTurn(gameId, nextPlayerId);
      
      // Clear inputs and re-enable form
      dart1Input.value = '';
      dart2Input.value = '';
      dart3Input.value = '';
      dartTotalEl.textContent = '0';
      submitButton.disabled = false;
      bustButton.disabled = false;
      
    } catch (error) {
      console.error('Error processing bust:', error);
      alert('Error processing bust: ' + error.message);
      
      // Re-enable the form on error
      scoreForm.querySelector('button[type="submit"]').disabled = false;
      bustButton.disabled = false;
    }
  });
  
  // Edit last score button
  const editLastScoreBtn = document.getElementById('edit-last-score');
  editLastScoreBtn.addEventListener('click', () => {
    // Toggle editing mode
    isEditingLastScore = !isEditingLastScore;
    
    if (isEditingLastScore) {
      // Change button text
      editLastScoreBtn.textContent = 'Cancel Edit';
      editLastScoreBtn.classList.add('editing');
      
      // If we have last scores data, pre-fill the inputs
      if (editLastScoreBtn.dataset.lastScores) {
        try {
          const lastScores = JSON.parse(editLastScoreBtn.dataset.lastScores);
          dart1Input.value = lastScores[0] || 0;
          dart2Input.value = lastScores[1] || 0;
          dart3Input.value = lastScores[2] || 0;
          calculateTotal();
        } catch (e) {
          console.error('Error parsing last scores:', e);
        }
      }
    } else {
      // Exit editing mode
      editLastScoreBtn.textContent = 'Edit Last Score';
      editLastScoreBtn.classList.remove('editing');
      
      // Clear the inputs
      dart1Input.value = '';
      dart2Input.value = '';
      dart3Input.value = '';
      calculateTotal();
    }
  });
  
  // Handle score submission
  scoreForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    
    const playerId = localStorage.getItem('playerId');
    
    // Get dart values and calculate total
    const dart1 = parseInt(dart1Input.value) || 0;
    const dart2 = parseInt(dart2Input.value) || 0;
    const dart3 = parseInt(dart3Input.value) || 0;
    const dartScores = [dart1, dart2, dart3];
    const points = dart1 + dart2 + dart3;
    
    if (dart1 < 0 || dart2 < 0 || dart3 < 0) {
      alert('Scores cannot be negative');
      return;
    }
    
    try {
      // Disable the form while processing
      const submitButton = scoreForm.querySelector('button[type="submit"]');
      submitButton.disabled = true;
      
      // Get current game data with one-time fetch
      const gameData = await fetchGameData(gameId);
      const player = gameData.players[playerId];
      const gameType = gameData.type;
      
      // Validate total score based on game type
      const isCountdownGame = gameType === '501' || gameType === '301' || gameType === '201';
      
      if (isCountdownGame) {
        const maxAllowed = Math.min(180, player.score);
        if (points < 0 || points > maxAllowed) {
          alert(`Please enter a valid score (0-${maxAllowed})`);
          submitButton.disabled = false;
          return;
        }
      } else {
        // For cricket/count-up, check maximum
        if (points < 0 || points > 180) {
          alert('Please enter a valid score (0-180)');
          submitButton.disabled = false;
          return;
        }
      }
      
      // Calculate new score
      let newScore;
      if (isCountdownGame) {
        if (player.score - points < 0) {
          // Bust - score remains the same
          newScore = player.score;
          alert('Bust! Score exceeds remaining points.');
        } else if (player.score - points === 0) {
          // Game won! (Win is handled by game over screen)
          newScore = 0;
        } else {
          // Normal score reduction
          newScore = player.score - points;
        }
      } else {
        // For cricket/count-up modes, just add points
        newScore = player.score + points;
      }
      
      // If editing last score
      if (isEditingLastScore) {
        // Get player's current score before the last turn
        const turns = player.turns ? Object.values(player.turns) : [];
        let lastTurnPoints = 0;
        
        if (turns.length > 0) {
          lastTurnPoints = turns[turns.length - 1].points || 0;
        }
        
        // Adjust score based on the difference
        if (isCountdownGame) {
          // For countdown games, add back the last turn points, then subtract the new points
          newScore = player.score + lastTurnPoints - points;
        } else {
          // For count-up games, subtract the last turn points, then add the new points
          newScore = player.score - lastTurnPoints + points;
        }
        
        // Update the last turn
        await updateLastTurn(gameId, playerId, points, dartScores);
        
        // Exit editing mode
        isEditingLastScore = false;
        editLastScoreBtn.textContent = 'Edit Last Score';
        editLastScoreBtn.classList.remove('editing');
      } else {
        // Record a new turn and update game stats
        await recordTurn(gameId, playerId, points, dartScores);
        await updateGameStats(gameId, points);
        
        // For countdown games, check for win condition
        const isCountdownGame = gameType === '501' || gameType === '301' || gameType === '201';
        const isWin = isCountdownGame && newScore === 0;
        
        if (isWin) {
          // Set the current player as winner
          await setGameWinner(gameId, playerId);
        } else {
          // Move to next player
          const playerIds = Object.keys(gameData.players);
          const currentIndex = playerIds.indexOf(playerId);
          const nextIndex = (currentIndex + 1) % playerIds.length;
          const nextPlayerId = playerIds[nextIndex];
          
          await setCurrentTurn(gameId, nextPlayerId);
        }
      }
      
      // Update the player's score
      await updateScore(gameId, playerId, newScore);
      
      // Clear inputs and re-enable form
      dart1Input.value = '';
      dart2Input.value = '';
      dart3Input.value = '';
      dartTotalEl.textContent = '0';
      submitButton.disabled = false;
      
    } catch (error) {
      console.error('Error processing score:', error);
      alert('Error processing score: ' + error.message);
      
      // Re-enable the form on error
      scoreForm.querySelector('button[type="submit"]').disabled = false;
    }
  });
  
  // Start game button (for first player/host)
  document.getElementById('start-game').addEventListener('click', async () => {
    try {
      // Disable button while starting game
      const startButton = document.getElementById('start-game');
      startButton.disabled = true;
      startButton.textContent = 'Starting...';
      
      // Fetch game data once instead of using real-time listener
      const gameData = await fetchGameData(gameId);
      
      if (!gameData.currentTurn) {
        const playerIds = Object.keys(gameData.players);
        if (playerIds.length > 0) {
          await setCurrentTurn(gameId, playerIds[0]);
        }
      }
      
      // Re-enable button
      startButton.disabled = false;
      startButton.textContent = 'Start Game';
      
    } catch (error) {
      console.error('Error starting game:', error);
      alert('Error starting game: ' + error.message);
      
      // Re-enable button on error
      const startButton = document.getElementById('start-game');
      startButton.disabled = false;
      startButton.textContent = 'Start Game';
    }
  });
}